const IMetricsClient = require("./IMetricsClient.js");

const FLUSH_TIMEOUT_MS = 2000;
const GROUP_EVENT = "group";

/**
 * Server-side metrics client backed by Sentry's Trace Metrics API
 * (`Sentry.metrics.*`). Inject `@sentry/node`.
 *
 * Unlike {@link SentryBrowserClient}, this client never attaches identity to
 * the Sentry scope. `identify` is a deliberate no-op and `group` emits a
 * counter rather than calling `setContext`, because Sentry metrics on the
 * backend are intentionally identifier-free — a backend user id in the Sentry
 * scope would turn request telemetry into queryable user data.
 *
 * The SDK may be null when no DSN is configured, in which case every method is
 * an inert no-op. SDK failures are reported through `onError` and never
 * propagate: a metrics call must not break business logic.
 */
class SentryNodeClient extends IMetricsClient {
  /**
   * @param {object} sentry The Sentry SDK (`@sentry/node`), or null when no DSN
   *   is configured.
   * @param {object} [options]
   * @param {string[]} [options.allowedAttributes] Event properties permitted as
   *   metric attributes. Defaults to none: each backend service has its own
   *   allowlist, and there is no safe cross-service default. Omitting it emits
   *   the event with no attributes rather than falling back to a wider list —
   *   this boundary keeps PII and high-cardinality ids out of Sentry, so it
   *   fails closed.
   * @param {(error: Error) => void} [options.onError] See {@link IMetricsClient}.
   */
  constructor(sentry, { allowedAttributes = [], onError } = {}) {
    super({ onError });
    this.sentry = sentry;
    this.allowedAttributes = new Set(allowedAttributes);
  }

  /**
   * Keep only allowlisted properties that carry a value. Null and undefined are
   * dropped rather than emitted as empty attributes.
   *
   * Note this differs from {@link SentryBrowserClient}, which keeps an
   * allowlisted key whose value is null. Both preserve their runtime's
   * long-standing behavior; neither is a bug.
   *
   * @private
   */
  filterAttributes(properties) {
    const attributes = {};
    const props = properties || {};
    for (const key of Object.keys(props)) {
      const value = props[key];
      if (this.allowedAttributes.has(key) && value !== undefined && value !== null) {
        attributes[key] = value;
      }
    }
    return attributes;
  }

  /**
   * Resolve the SDK's counter method by name so it stays bound to
   * `sentry.metrics`. `count` is the current API; `increment` is carried as a
   * fallback because one service resolved it defensively and dropping it here
   * would silently stop that service emitting.
   *
   * @private
   * @returns {string|null}
   */
  counterMethod() {
    const metrics = this.sentry && this.sentry.metrics;
    if (!metrics) return null;
    if (typeof metrics.count === "function") return "count";
    if (typeof metrics.increment === "function") return "increment";
    return null;
  }

  /** @private */
  emitCounter(name, attributes) {
    const method = this.counterMethod();
    if (!method) return undefined;
    return this.safeInvoke(() =>
      this.sentry.metrics[method](name, 1, { attributes })
    );
  }

  /**
   * Record an event as a Sentry counter. `userId` is accepted for interface
   * parity but deliberately never emitted — there is no per-user attribution in
   * the metrics product.
   */
  // oxlint-disable-next-line no-unused-vars
  async track(userId, eventName, properties = {}) {
    return this.emitCounter(eventName, this.filterAttributes(properties));
  }

  /**
   * Deliberate no-op. Sentry metrics on the backend are identifier-free; this
   * method exists so call sites shared with the browser client keep their
   * shape. It must never reach `Sentry.setUser`.
   */
  // oxlint-disable-next-line no-unused-vars
  async identify(userId, traits = {}) {}

  /**
   * Record group association as a counter named `group`, carrying only
   * allowlisted group traits — never the group or user id. This is not
   * `setContext`: on the backend there is no per-request scope to attach to.
   */
  // oxlint-disable-next-line no-unused-vars
  async group(groupId, userId, groupTraits = {}) {
    return this.emitCounter(GROUP_EVENT, this.filterAttributes(groupTraits));
  }

  async flush() {
    if (this.sentry && typeof this.sentry.flush === "function") {
      await this.safeInvoke(() => this.sentry.flush(FLUSH_TIMEOUT_MS));
    }
  }
}

module.exports = SentryNodeClient;
